<script setup lang="ts">
import { ref, useAttrs, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	focus: {
		type: Boolean,
		default: false,
	},
	value: {
		type: String,
		required: true,
	},
	isIndex: {
		type: Boolean,
		default: false,
	},
})

defineOptions({
	inheritAttrs: false,
})

const emit = defineEmits(['close', 'update:value', 'search', 'focus', 'blur'])
const attrs = useAttrs()
const isFocus = ref(props.focus)
const attrsDisabled = computed(() => attrs.disabled as boolean | undefined)
const svgOptions = ['close_border_v2', 'close_v1', 'search_v2', 'search']
const closeSmSvg = ref(svgOptions[0])
const closeMdSvg = ref(svgOptions[1])
const searchSmSvg = ref(svgOptions[2])
const searchMdSvg = ref(svgOptions[3])
const isIndex = ref(props.isIndex)
const searchBar = ref(null)

const isInputEmpty = computed(() => !props.value.trim())

onClickOutside(searchBar, event => {
	if (event && isFocus.value) {
		handleBlur()
	}
})

function handleClose(event: MouseEvent) {
	event.preventDefault()
	emit('close')
	emit('update:value', '')
}

function handleSearch() {
	if (!isInputEmpty.value) {
		emit('search')
	}
}

function handleBlur() {
	isFocus.value = false
	emit('blur')
}

function handleFocus() {
	isFocus.value = true
	emit('focus')
}

function handleInput(e: Event) {
	const target = e.target as HTMLInputElement
	emit('update:value', target.value.trim())
}

const isShowCloseButton = computed(() => {
	return props.value && isFocus.value ? 'visible' : 'hidden'
})
</script>

<template>
	<div
		ref="searchBar"
		:class="[
			'bs-search-wrapper',
			attrsDisabled ? 'disabled' : '',
			isFocus ? 'focus' : '',
			isIndex ? '' : 'isInSearch',
		]"
		@keyup.enter="!isInputEmpty && $emit('search')"
	>
		<BaseIcon :name="searchSmSvg" class="block h-24px w-24px mdp:hidden" />
		<input
			class="bs-search-input"
			ref="input"
			type="text"
			v-bind="{ ...$attrs }"
			:value="value"
			@focus="handleFocus"
			@input="handleInput"
		/>
		<button
			class="bs-search-button close"
			:disabled="attrsDisabled"
			type="button"
			:class="isShowCloseButton"
			@mousedown="handleClose"
		>
			<BaseIcon :name="closeSmSvg" class="block mdp:hidden" />
			<BaseIcon :name="closeMdSvg" class="hidden mdp:block" />
		</button>
		<button
			class="bs-search-button search hidden mdp:block"
			type="submit"
			:disabled="attrsDisabled || isInputEmpty"
			@click="handleSearch"
		>
			<BaseIcon :name="searchMdSvg" class="hidden mdp:block" />
		</button>
	</div>
</template>
